import { b as __awaiter, c as __generator, _ as _assign } from '../tslib.es6-604585c1.js';
import { useEffect, useCallback, useRef } from 'react';
import { unstable_batchedUpdates } from 'react-dom';
import AnalyticsService from '@picsart/rc/services/analytics';
import { authStates, authStateKey } from '../models/user/index.js';
import { u as useAuthContext, a as authStatus } from '../authContext-4a1a5892.js';
import { deleteCookie } from '../cookie/index.js';
import { a as authCookies } from '../constants-564e5651.js';
import { setStorage, getStorage } from '../services/browserStorage/index.js';
import errorLogger, { BUGSNAG_STATUSES } from '../services/errorLogger/index.js';
import CleverTapService from '../services/clevertapService/index.js';
import getDeviceId from '../getDeviceId/index.js';
import { REFRESH_TOKEN_URL, API_URL } from '../configs/constants/index.js';
import { authorizationHeader } from '../services/auth/index.js';
import useIsomorphicLayoutEffect from '../useIsomorphicLayoutEffect/index.js';
import usePageVisibility from '../usePageVisibility/index.js';

var isAbortOrNavigationError = function isAbortOrNavigationError(err) {
  var _a, _b;
  var error = err;
  return (error === null || error === void 0 ? void 0 : error.name) === 'AbortError' || (error === null || error === void 0 ? void 0 : error.message) === 'Failed to fetch' || ((_b = (_a = error === null || error === void 0 ? void 0 : error.message) === null || _a === void 0 ? void 0 : _a.includes) === null || _b === void 0 ? void 0 : _b.call(_a, 'NetworkError'));
};

var refreshTokenRequest = function refreshTokenRequest(headers, getRefreshTokenHeader) {
  if (headers === void 0) {
    headers = {};
  }
  if (getRefreshTokenHeader === void 0) {
    getRefreshTokenHeader = null;
  }
  return __awaiter(void 0, void 0, void 0, function () {
    return __generator(this, function (_a) {
      return [2, fetch(REFRESH_TOKEN_URL, {
        headers: _assign(_assign({
          'content-type': 'application/json',
          platform: 'website'
        }, getDeviceId() && {
          deviceid: getDeviceId()
        }), headers),
        credentials: 'include',
        body: JSON.stringify({}),
        method: 'POST'
      }).then(function (res) {
        var _a;
        if (res.status >= 400) {
          throw new Error(res.status.toString());
        }
        getRefreshTokenHeader === null || getRefreshTokenHeader === void 0 ? void 0 : getRefreshTokenHeader((_a = res === null || res === void 0 ? void 0 : res.headers) === null || _a === void 0 ? void 0 : _a.get('set-cookie'));
        return res.json();
      })["catch"](function (err) {
        throw err;
      })];
    });
  });
};
var refreshToken = function refreshToken(headers, getRefreshTokenHeader) {
  if (headers === void 0) {
    headers = {};
  }
  if (getRefreshTokenHeader === void 0) {
    getRefreshTokenHeader = null;
  }
  return __awaiter(void 0, void 0, void 0, function () {
    return __generator(this, function (_a) {
      return [2, refreshTokenRequest(headers, getRefreshTokenHeader)["catch"](function () {
        return refreshTokenRequest(headers, getRefreshTokenHeader);
      })];
    });
  });
};

function getUser(accessToken) {
  return __awaiter(this, void 0, void 0, function () {
    var userData;
    return __generator(this, function (_a) {
      switch (_a.label) {
        case 0:
          return [4, fetch("".concat(API_URL, "/users/show/me.json?email_encoded=1"), {
            headers: authorizationHeader(accessToken),
            priority: 'high'
          })];
        case 1:
          userData = _a.sent();
          return [2, userData.json()];
      }
    });
  });
}

var userLogout = function userLogout(accessToken) {
  return __awaiter(void 0, void 0, void 0, function () {
    return __generator(this, function (_a) {
      return [2, fetch("".concat(API_URL, "/users/signout.json"), {
        method: 'POST',
        headers: _assign({
          platform: 'website'
        }, authorizationHeader(accessToken)),
        credentials: 'include',
        body: JSON.stringify({})
      })];
    });
  });
};

var refreshTokenInsurance = 60;
var refreshTokenTimerId = null;
var BACKEND_REQUEST_TIME_TRESHOLD = 60;
var REFRESH_TOKEN_EXPIRATION_TIME_BUFFER = 2 * 60 * 1000;
var checkTokenExpiration = function checkTokenExpiration(token, tokenExpirationDate) {
  if (!token || !tokenExpirationDate) return false;
  var now = new Date();
  var nowWithBuffer = new Date(now.getTime() - REFRESH_TOKEN_EXPIRATION_TIME_BUFFER);
  return tokenExpirationDate < nowWithBuffer;
};
var getTokenExpirationDate = function getTokenExpirationDate(expiresIn) {
  if (!expiresIn) return null;
  var now = new Date();
  return new Date(now.getTime() + (expiresIn - BACKEND_REQUEST_TIME_TRESHOLD) * 1000);
};
function useBearerAuthentication(disableRefreshCycle) {
  var _a = useAuthContext(),
    onTokenChange = _a.onTokenChange,
    onStatusChange = _a.onStatusChange,
    onUserChange = _a.onUserChange,
    user = _a.user,
    tokenData = _a.tokenData,
    tokenDataRef = _a.tokenDataRef,
    setTokenExpirationDate = _a.setTokenExpirationDate,
    tokenExpirationDate = _a.tokenExpirationDate;
  useEffect(function () {
    tokenDataRef && tokenDataRef.current !== tokenData && (tokenDataRef.current = tokenData || null);
  }, [tokenData, tokenDataRef]);
  var tokenChange = useCallback(function (token) {
    tokenDataRef && (tokenDataRef.current = token || null);
    onTokenChange === null || onTokenChange === void 0 ? void 0 : onTokenChange(token);
    setTokenExpirationDate === null || setTokenExpirationDate === void 0 ? void 0 : setTokenExpirationDate((token === null || token === void 0 ? void 0 : token.expires_in) ? getTokenExpirationDate(token.expires_in) : null);
  }, [onTokenChange, setTokenExpirationDate, tokenDataRef]);
  var isTokenFetchedRef = useRef(false);
  var deleteAuthCookies = useCallback(function () {
    authCookies.forEach(function (cookieKey) {
      deleteCookie(cookieKey);
    });
  }, []);
  var makeLogout = useCallback(function (logoutCallback) {
    var _a;
    var currentToken = (_a = tokenDataRef === null || tokenDataRef === void 0 ? void 0 : tokenDataRef.current) === null || _a === void 0 ? void 0 : _a.access_token;
    unstable_batchedUpdates(function () {
      setStorage(authStateKey, authStates.unauthorized);
      deleteAuthCookies();
      refreshTokenTimerId && clearTimeout(refreshTokenTimerId);
      refreshTokenTimerId = null;
      tokenChange(undefined);
      setTokenExpirationDate === null || setTokenExpirationDate === void 0 ? void 0 : setTokenExpirationDate(null);
      onUserChange === null || onUserChange === void 0 ? void 0 : onUserChange(null);
      onStatusChange === null || onStatusChange === void 0 ? void 0 : onStatusChange(authStatus.failed);
      AnalyticsService.pushDataLayer({
        pulse: 'state',
        user_id: null
      });
    });
    if (currentToken) {
      userLogout(currentToken).then(function () {
        logoutCallback === null || logoutCallback === void 0 ? void 0 : logoutCallback();
      })["catch"](function (error) {
        errorLogger.trigger(error, BUGSNAG_STATUSES.ERROR);
      });
    } else {
      logoutCallback === null || logoutCallback === void 0 ? void 0 : logoutCallback();
    }
  }, [deleteAuthCookies, onStatusChange, onUserChange, tokenChange, tokenDataRef, setTokenExpirationDate]);
  var startTokenLifecycle = useCallback(function (props) {
    if (!(props === null || props === void 0 ? void 0 : props.access_token)) {
      makeLogout();
      return;
    }
    tokenChange(props);
    setStorage(authStateKey, authStates.authorized);
    var refreshTokenExpiresIn = (props.expires_in - refreshTokenInsurance) * 1000;
    onStatusChange === null || onStatusChange === void 0 ? void 0 : onStatusChange(function (prev) {
      if (user) {
        return authStatus.resolved;
      }
      if (prev !== authStatus.resolved) {
        return authStatus.done;
      }
      return prev;
    });
    if (disableRefreshCycle) {
      return;
    }
    refreshTokenTimerId && clearTimeout(refreshTokenTimerId);
    refreshTokenTimerId = setTimeout(function () {
      refreshToken().then(function (tokenRes) {
        startTokenLifecycle(tokenRes.response);
      })["catch"](function (err) {
        if (!isAbortOrNavigationError(err)) {
          makeLogout();
        }
      });
    }, refreshTokenExpiresIn);
  }, [disableRefreshCycle, makeLogout, onStatusChange, tokenChange, user]);
  var extractUserInfo = function extractUserInfo(data) {
    var registered = data.registered,
      subscription = data.subscription;
    var is_new = !!registered;
    var subscribed = subscription && subscription.granted;
    var isTrialUsed = subscription ? !!subscription.is_trial_used : false;
    return _assign(_assign({}, data), {
      isTrialUsed: isTrialUsed,
      subscribed: subscribed,
      is_new: is_new
    });
  };
  var refreshTokenPromiseRef = useRef(null);
  var fetchUser = useCallback(function (accessToken) {
    onUserChange === null || onUserChange === void 0 ? void 0 : onUserChange(function (prev) {
      if (!prev) {
        getUser(accessToken).then(function (userData) {
          onUserChange === null || onUserChange === void 0 ? void 0 : onUserChange(extractUserInfo(userData));
          onStatusChange === null || onStatusChange === void 0 ? void 0 : onStatusChange(authStatus.resolved);
          AnalyticsService.pushDataLayer({
            pulse: 'state',
            user_id: (userData === null || userData === void 0 ? void 0 : userData.id) || null
          });
        })["catch"](function (err) {
          if (!isAbortOrNavigationError(err)) {
            makeLogout();
          }
        });
      }
      if (prev) {
        CleverTapService.scheduleUserLogin(prev);
      }
      return prev;
    });
  }, [makeLogout, onStatusChange, onUserChange]);
  var isVisible = usePageVisibility();
  var refreshTokenAndUser = useCallback(function () {
    if (disableRefreshCycle) return Promise.resolve();
    if (!refreshTokenPromiseRef.current) {
      refreshTokenPromiseRef.current = refreshToken().then(function (res) {
        var _a;
        startTokenLifecycle(res === null || res === void 0 ? void 0 : res.response);
        if ((_a = res === null || res === void 0 ? void 0 : res.response) === null || _a === void 0 ? void 0 : _a.access_token) {
          fetchUser(res.response.access_token);
        }
      })["catch"](function (error) {
        if (!isAbortOrNavigationError(error)) {
          makeLogout();
        }
      })["finally"](function () {
        refreshTokenPromiseRef.current = null;
      });
    }
    return refreshTokenPromiseRef.current;
  }, [disableRefreshCycle, fetchUser, makeLogout, startTokenLifecycle]);
  useIsomorphicLayoutEffect(function () {
    if (!isTokenFetchedRef.current && !(tokenDataRef === null || tokenDataRef === void 0 ? void 0 : tokenDataRef.current)) {
      isTokenFetchedRef.current = true;
      var authState = getStorage(authStateKey);
      if (authState === authStates.authorized) {
        refreshTokenAndUser();
      } else {
        onStatusChange === null || onStatusChange === void 0 ? void 0 : onStatusChange(authStatus.failed);
      }
    } else if (!isTokenFetchedRef.current && (tokenDataRef === null || tokenDataRef === void 0 ? void 0 : tokenDataRef.current)) {
      isTokenFetchedRef.current = true;
      startTokenLifecycle(tokenDataRef === null || tokenDataRef === void 0 ? void 0 : tokenDataRef.current);
    }
  }, [refreshTokenAndUser]);
  useEffect(function () {
    if (!isVisible || !(tokenDataRef === null || tokenDataRef === void 0 ? void 0 : tokenDataRef.current) || !tokenExpirationDate) return;
    var isTokenAboutToExpire = checkTokenExpiration(tokenDataRef === null || tokenDataRef === void 0 ? void 0 : tokenDataRef.current, tokenExpirationDate);
    if (isTokenAboutToExpire) {
      refreshTokenAndUser();
    }
  }, [isVisible, refreshTokenAndUser, tokenDataRef, tokenExpirationDate]);
  useEffect(function () {
    onUserChange === null || onUserChange === void 0 ? void 0 : onUserChange(function (prevUser) {
      if (prevUser) {
        onStatusChange === null || onStatusChange === void 0 ? void 0 : onStatusChange(function (prev) {
          return prev === authStatus.done ? authStatus.resolved : prev;
        });
      }
      return prevUser;
    });
  }, [onStatusChange, onUserChange]);
  return {
    makeLogout: makeLogout,
    refreshTokenAndUser: refreshTokenAndUser
  };
}

export { useBearerAuthentication as default };
//# sourceMappingURL=index.js.map
